import { useState } from "react"
import { Box, Button, Center, HStack, Spinner, Text, VStack } from "@chakra-ui/react"
import { useIntegrationSources } from "./hooks/useIntegrationSources"
import { useConnectorTypes } from "./hooks/useConnectorTypes"
import { useDeleteIntegrationSource } from "./hooks/useDeleteIntegrationSource"
import { ConnectorCard } from "./ConnectorCard"
import { AddConnectorPopup } from "./AddConnectorPopup"
import { DeleteBlockedDialog } from "./DeleteBlockedDialog"
import { DeleteConfirmDialog } from "./DeleteConfirmDialog"
import { shouldBlockDeletion } from "./deletionGuard"
import { emitSectionEvent, SECTION_EVENTS } from "@hydrologiq/shared/hooks/useSectionEvent"

/**
 * DataConnectorsTab - Displays list of data connector cards with loading/error states.
 *
 * Fetches integration sources for the active organisation and resolves
 * connector type display names via the connector types query.
 * Shows a loading spinner while data is in flight, an error message on failure,
 * and an "Add new connector" button for creating new connectors.
 */
export function DataConnectorsTab({
  onNavigate,
  isPlatformAdmin = false,
}: { onNavigate?: () => void; isPlatformAdmin?: boolean } = {}) {
  const { sources, loading, error, refetch } = useIntegrationSources()
  const { connectorTypes } = useConnectorTypes()
  const { deleteSource, loading: deleting } = useDeleteIntegrationSource()
  const [showAddPopup, setShowAddPopup] = useState(false)
  const [showDeleteBlocked, setShowDeleteBlocked] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [pendingDeleteName, setPendingDeleteName] = useState("")
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const getTypeDisplayName = (type: string): string => {
    const found = connectorTypes.find(
      (ct: { type: string; displayName: string }) => ct.type === type
    )
    return found?.displayName ?? type
  }

  const handleDelete = (connectorId: string, systemCount: number) => {
    setDeleteError(null)

    if (shouldBlockDeletion(systemCount)) {
      setShowDeleteBlocked(true)
      return
    }

    // Find the connector name for the confirmation message
    const connector = sources.find((s: any) => s.id === connectorId)
    setPendingDeleteId(connectorId)
    setPendingDeleteName(connector?.name ?? "this connector")
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return

    try {
      await deleteSource({ variables: { input: { sourceId: pendingDeleteId } } })
      setShowDeleteConfirm(false)
      setPendingDeleteId(null)
      setPendingDeleteName("")
      refetch()
      emitSectionEvent(SECTION_EVENTS.CONNECTOR_DELETED)
    } catch (err: any) {
      setShowDeleteConfirm(false)
      setPendingDeleteId(null)
      setPendingDeleteName("")
      setDeleteError(err?.message ?? "Failed to delete connector. Please try again.")
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
    setPendingDeleteId(null)
    setPendingDeleteName("")
  }

  if (loading) {
    return (
      <Center py={8}>
        <HStack gap={3}>
          <Spinner size="lg" />
          <Text>Loading data connectors...</Text>
        </HStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Box py={4}>
        <Text color="red.500">
          Failed to load data connectors. Please try again later.
        </Text>
      </Box>
    )
  }

  return (
    <VStack align="stretch" gap={4} py={4}>
      <HStack justify="flex-end">
        <Button variant="hydrologiq_primary" size="sm" onClick={() => setShowAddPopup(true)}>
          Add new connector
        </Button>
      </HStack>

      <AddConnectorPopup
        open={showAddPopup}
        isPlatformAdmin={isPlatformAdmin}
        onClose={() => setShowAddPopup(false)}
        onSuccess={() => {
          refetch()
          emitSectionEvent(SECTION_EVENTS.CONNECTOR_CREATED)
        }}
      />

      <DeleteBlockedDialog
        open={showDeleteBlocked}
        onClose={() => setShowDeleteBlocked(false)}
      />

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        connectorName={pendingDeleteName}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        loading={deleting}
      />

      {deleteError && (
        <Box bg="#FEF2F2" border="1px solid" borderColor="#F87171" borderRadius="md" p={3}>
          <Text color="#B91C1C" fontWeight="semibold" fontSize="sm">
            Failed to delete connector
          </Text>
          <Text color="#DC2626" fontSize="sm" mt={1}>
            {deleteError}
          </Text>
        </Box>
      )}

      {sources.length === 0 ? (
        <Center py={8}>
          <Text color="gray.500">No data connectors configured yet.</Text>
        </Center>
      ) : (
        <VStack align="stretch" gap={3}>
          {sources.map((connector: any) => (
            <ConnectorCard
              key={connector.id}
              connector={connector}
              typeDisplayName={getTypeDisplayName(connector.type)}
              onDelete={connector.type !== "RMS" ? handleDelete : undefined}
              onNavigate={onNavigate}
            />
          ))}
        </VStack>
      )}
    </VStack>
  )
}
